import * as React from 'react';

import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DropTargetMonitor, XYCoord } from 'react-dnd';

/*
    Ce composant est un wrapper qui permet de drag n drop entre eux les elements de votre choix.
    
    Scope fonctionnel actuel :

    - Il ne fonctionne que sur des listes verticales
*/

export type JRCDragAndDropProps<T> = {
    elms: T[];
    renderEl: (el: T, index: number) => React.ReactNode;
    onDrag: (dragIndex: number, hoverIndex: number) => void;
    onDrop?: (dragIndex: number, hoverIndex: number) => void;
};

export const BETA_JRCDragAndDrop = <T,>({ elms, renderEl, onDrag, onDrop }: JRCDragAndDropProps<T>) => {
    return (
        <DndProvider backend={HTML5Backend}>
            {elms.map((el, index) => (
                <JRCDragAndDropItem onDrag={onDrag} onDrop={onDrop} index={index} key={index}>
                    <>{renderEl(el, index)}</>
                </JRCDragAndDropItem>
            ))}
        </DndProvider>
    );
};

type JRCDragAndDropItemProps = {
    children: React.ReactElement;
    index: number;
    onDrag: (dragIndex: number, dropIndex: number) => void;
    onDrop?: (dragIndex: number, dropIndex: number) => void;
};

const JRCDragAndDropItem = ({ children, index, onDrag, onDrop }: JRCDragAndDropItemProps) => {
    const ref = React.useRef<HTMLTableRowElement>(null);

    const [, drop] = useDrop({
        accept: 'div',
        drop(item: any) {
            if (!ref.current) {
                return;
            }

            if (!onDrop) {
                return;
            }

            const dragIndex = item.index;

            onDrop(dragIndex, 0);
        },
        hover(item: any, monitor: DropTargetMonitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            const clientOffset = monitor.getClientOffset();

            // Get pixels to the top
            const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            onDrag(dragIndex, hoverIndex);

            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: 'div',
        item: () => {
            return { index };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));
    return (
        <div ref={ref} style={isDragging ? { opacity } : {}}>
            {children}
        </div>
    );
};
